import type { GPTModelItem } from "@/api/models"
import type { ReasoningEffortOverride, ReasoningMode } from "@/stores/ai-explorer-store"

import {
  type ModelFamilyHint,
  modelFamilyHint,
  sortCurrentModels,
} from "@/features/chat/components/reasoning-model-utils"

// Keyboard-shortcut matching + effort stepping for the chat composer's model
// selector (Alt+0–6 pick a model, Alt+plus/minus step reasoning effort). Pure
// functions, kept out of the hook so the layout/AltGr edge cases are
// unit-testable without synthesizing real browser keyboard events.

/** 0 = a family's first deployment (Alt+1–3), 1 = its second, e.g. Global (Alt+4–6). */
export type ModelShortcutVariant = 0 | 1

export type ModelToggleAction =
  | { kind: "pick-model"; family: ModelFamilyHint; variant: ModelShortcutVariant }
  | { kind: "step-effort"; direction: 1 | -1 }

// Digits are matched on the physical key (`event.code`) so the mapping holds on
// layouts where digits are shifted (AZERTY) or where Option+digit produces a
// character (macOS): Alt+0 Auto, Alt+1 Luna, Alt+2 Terra, Alt+3 Sol — the same
// top-to-bottom order as the picker's rows. A family can be deployed twice
// (e.g. "Luna" and "Luna (Global)"); its second deployment gets the digit + 3:
// Alt+4 Luna (Global), Alt+5 Terra (Global), Alt+6 Sol (Global), so every row
// has its own shortcut (#3322). Numpad digits are deliberately NOT mapped: on
// Windows, Alt + numpad digits types Alt-codes (e.g. Alt+0169 = ©), which must
// keep producing characters instead of switching models.
const CODE_TO_SHORTCUT: Partial<
  Record<string, { family: ModelFamilyHint; variant: ModelShortcutVariant }>
> = {
  Digit0: { family: "auto", variant: 0 },
  Digit1: { family: "luna", variant: 0 },
  Digit2: { family: "terra", variant: 0 },
  Digit3: { family: "sol", variant: 0 },
  Digit4: { family: "luna", variant: 1 },
  Digit5: { family: "terra", variant: 1 },
  Digit6: { family: "sol", variant: 1 },
}

const FAMILY_DIGIT: Record<ModelFamilyHint, number> = { auto: 0, luna: 1, terra: 2, sol: 3 }
const SECOND_VARIANT_OFFSET = 3

// Plus/minus are matched on the produced character (`event.key`) because their
// physical positions vary per layout (Swedish "+" sits on code "Minus"). The
// extra entries are what macOS Option+plus/minus actually produce (±, ≠, –).
const EFFORT_UP_KEYS = new Set(["+", "=", "±", "≠"])
const EFFORT_DOWN_KEYS = new Set(["-", "–", "−", "—"])

type KeyComboEvent = Pick<
  KeyboardEvent,
  "altKey" | "ctrlKey" | "metaKey" | "shiftKey" | "key" | "code"
>

/**
 * Map a keydown to a model-toggle action, or null when the event is not ours.
 * Requires plain Alt: `!ctrlKey` is the AltGr guard — Windows reports AltGr as
 * Ctrl+Alt, so typing "@" (AltGr+2) or "\" (AltGr+plus) on a Swedish layout
 * must never switch models; `!metaKey` keeps Cmd+Option combos free on macOS.
 */
export function matchModelToggleAction(event: KeyComboEvent): ModelToggleAction | null {
  if (!event.altKey || event.ctrlKey || event.metaKey) return null
  // Digits reject Shift: Alt+Shift is Windows' default input-language toggle,
  // so a digit brushed mid-switch must not change models. Plus/minus keep
  // allowing Shift — on US layouts "+" itself is Shift+Equal.
  const shortcut = CODE_TO_SHORTCUT[event.code]
  if (shortcut && !event.shiftKey) return { kind: "pick-model", ...shortcut }
  if (EFFORT_UP_KEYS.has(event.key) || event.code === "NumpadAdd") {
    return { kind: "step-effort", direction: 1 }
  }
  if (EFFORT_DOWN_KEYS.has(event.key) || event.code === "NumpadSubtract") {
    return { kind: "step-effort", direction: -1 }
  }
  return null
}

/**
 * Shared model-pick semantics for the picker row click and the Alt+digit
 * shortcut, so the two can never drift: set the override; slash modes keep
 * their admin-preset effort lock, while auto (or unset mode) clears the effort
 * override so the slider follows the newly selected model's default.
 */
export function applyModelPick(
  modelId: string,
  mode: ReasoningMode | null | undefined,
  handlers: {
    onModelOverrideChange: (modelId: string | null) => void
    onEffortOverrideChange?: (effort: ReasoningEffortOverride | null) => void
  }
): void {
  handlers.onModelOverrideChange(modelId)
  if (mode == null || mode === "auto") {
    handlers.onEffortOverrideChange?.(null)
  }
}

/** A family's deployments in picker order (regional before Global, then id). */
function familyVariants(models: GPTModelItem[], family: ModelFamilyHint): GPTModelItem[] {
  return sortCurrentModels(models).filter((model) => modelFamilyHint(model.id) === family)
}

/**
 * Find the catalog model a shortcut targets (undefined ⇒ shortcut no-ops).
 * Deterministic: the same catalog always yields the same model, whatever order
 * the catalog lists its deployments in.
 */
export function findModelByFamily(
  models: GPTModelItem[],
  family: ModelFamilyHint,
  variant: ModelShortcutVariant = 0
): GPTModelItem | undefined {
  return familyVariants(models, family)[variant]
}

/**
 * The effort the UI currently treats as active: valid override → the model's
 * documented default → the middle stop. Single source of truth shared by the
 * pill's slider and the keyboard shortcuts so both always agree.
 */
export function resolveActiveEffort(
  supportedEfforts: string[],
  effortOverride: string | null | undefined,
  defaultEffort: string | null | undefined
): ReasoningEffortOverride | undefined {
  if (supportedEfforts.length === 0) return undefined
  if (effortOverride && supportedEfforts.includes(effortOverride)) {
    return effortOverride as ReasoningEffortOverride
  }
  if (defaultEffort && supportedEfforts.includes(defaultEffort)) {
    return defaultEffort as ReasoningEffortOverride
  }
  return supportedEfforts[Math.floor(supportedEfforts.length / 2)] as ReasoningEffortOverride
}

/**
 * One step along the model's effort ladder. Returns the new effort, or null
 * when there is nothing to change (empty ladder, or already clamped at the
 * end the caller is stepping toward).
 */
export function stepEffort(
  supportedEfforts: string[],
  activeEffort: string | undefined,
  direction: 1 | -1
): ReasoningEffortOverride | null {
  if (supportedEfforts.length === 0) return null
  const currentIndex = activeEffort ? supportedEfforts.indexOf(activeEffort) : -1
  const baseIndex = currentIndex >= 0 ? currentIndex : Math.floor(supportedEfforts.length / 2)
  const nextIndex = Math.min(supportedEfforts.length - 1, Math.max(0, baseIndex + direction))
  if (nextIndex === currentIndex) return null
  return supportedEfforts[nextIndex] as ReasoningEffortOverride
}

/**
 * The dropdown's kbd hint for a current-tier row ("Alt+1"), null for rows
 * without one. Pass the picker's current-tier models so a family's second
 * deployment gets its own digit instead of repeating the first one's; a third
 * deployment of the same family has no shortcut.
 */
export function modelShortcutHint(
  modelId: string,
  currentModels: GPTModelItem[] = []
): string | null {
  const family = modelFamilyHint(modelId)
  if (family === null) return null
  const index = familyVariants(currentModels, family).findIndex((model) => model.id === modelId)
  // -1 (model not in the list) keeps the family digit, as before the list existed.
  if (index <= 0) return `Alt+${FAMILY_DIGIT[family]}`
  if (index === 1 && family !== "auto") return `Alt+${FAMILY_DIGIT[family] + SECOND_VARIANT_OFFSET}`
  return null
}
